import React, { useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AIIcon,
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Description as DocumentIcon,
  Storage as StorageIcon,
  Image as ImageIcon
} from '@mui/icons-material';

import { useLazyGetPreSignedUrlDocsQuery } from '../../redux/thunks/files/filespresignurldocs';
import { useDeleteBusinessDocumentMutation } from '../../redux/thunks/files/businessdocdelete';

// Document quota constants
const DOCUMENT_QUOTAS = {
  free: {
    maxDocuments: 1,
    maxFileSize: 5 * 1024 * 1024, // 5MB per file
    maxTotalStorage: 5 * 1024 * 1024 // 5MB total
  },
  premium: {
    maxDocuments: 20,
    maxFileSize: 50 * 1024 * 1024, // 50MB per file
    maxTotalStorage: 500 * 1024 * 1024 // 500MB total
  }
};

// Utility function to format file sizes
const formatFileSize = (value, inputUnit = 'bytes') => {
  if (value === 0) return '0 Bytes';
  
  let bytes = value;
  if (inputUnit === 'mb' || inputUnit === 'MB') {
    bytes = value * 1024 * 1024;
  } else if (inputUnit === 'kb' || inputUnit === 'KB') {
    bytes = value * 1024;
  }
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const sizeIndex = Math.max(0, Math.min(i, sizes.length - 1));
  
  return parseFloat((bytes / Math.pow(k, sizeIndex)).toFixed(2)) + ' ' + sizes[sizeIndex];
};

// Helper function to check if file is an image
const isImageFile = (fileName) => {
  return /\.(jpg|jpeg|png)$/i.test(fileName);
};

// Helper function to get file type icon
const getFileTypeIcon = (fileName) => {
  if (isImageFile(fileName)) {
    return <ImageIcon fontSize="small" color="primary" />;
  }
  return <DocumentIcon fontSize="small" color="primary" />;
};

// Document Quota Display Component
const DocumentQuotaDisplay = ({ userAccountType, currentDocuments, currentStorage, quotaLimits, isLoading }) => {
  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Loading document usage...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const documentUsagePercentage = (currentDocuments / quotaLimits.maxDocuments) * 100;
  const currentStorageBytes = currentStorage * 1024 * 1024;
  const storageUsagePercentage = (currentStorageBytes / quotaLimits.maxTotalStorage) * 100;
  const canUploadDocuments = currentDocuments < quotaLimits.maxDocuments;
  const hasStorageSpace = currentStorageBytes < quotaLimits.maxTotalStorage;

  return (
    <Card variant="outlined" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="medium">
              Document Quota Usage
            </Typography>
          </Box>
          <Chip 
            label={userAccountType === 'premium' ? 'Premium' : 'Free'}
            size="small"
            color={userAccountType === 'premium' ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
        
        {/* Document Count Usage */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Documents
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {currentDocuments}/{quotaLimits.maxDocuments}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(documentUsagePercentage, 100)}
            color={canUploadDocuments ? 'primary' : 'error'}
            sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
          />
          <Typography 
            variant="caption" 
            color={canUploadDocuments ? 'success.main' : 'error.main'}
          >
            {canUploadDocuments 
              ? `${quotaLimits.maxDocuments - currentDocuments} documents remaining`
              : 'Document limit reached'
            }
          </Typography>
        </Box>

        {/* Storage Usage */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Storage
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatFileSize(currentStorage, 'MB')}/{formatFileSize(quotaLimits.maxTotalStorage)}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(storageUsagePercentage, 100)}
            color={hasStorageSpace ? 'primary' : 'error'}
            sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
          />
          <Typography 
            variant="caption" 
            color={hasStorageSpace ? 'success.main' : 'error.main'}
          >
            {hasStorageSpace 
              ? `${formatFileSize(quotaLimits.maxTotalStorage - currentStorageBytes)} remaining`
              : 'Storage limit reached'
            }
          </Typography>
        </Box>

        <Typography 
          variant="caption" 
          color="textSecondary" 
          sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
        >
          Account: {userAccountType.charAt(0).toUpperCase() + userAccountType.slice(1)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const BusinessDocumentUpload = ({ 
  businessId, 
  businessName, 
  existingDocuments = [],
  onSuccess,
  onCancel,
  quotas,
  accountType = 'free'
}) => {
  const userId = useSelector((state) => state.auth?.userId || null);
  
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, success
  const [error, setError] = useState('');
  const [uploadErrors, setUploadErrors] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fileInputRef = useRef(null);

  // API hooks
  const [getPreSignedDocs, { isLoading: preSignedUrlDocsLoading }] = useLazyGetPreSignedUrlDocsQuery();
  const [deleteBusinessDocument, { isLoading: isDeleting }] = useDeleteBusinessDocumentMutation();

  // Calculate current usage based on provided data
  const currentDocuments = existingDocuments.length;
  const currentStorage = existingDocuments.reduce((total, doc) => total + (doc.size || 0), 0) / (1024 * 1024); // Convert to MB

  const canUploadDocuments = currentDocuments < quotas.maxDocuments;
  const hasStorageSpace = (currentStorage * 1024 * 1024) < quotas.maxTotalStorage;

  // Enhanced document validation - UPDATED TO ONLY ALLOW PDF, TXT, JPG, PNG
  const validateDocuments = (files) => {
    const validationErrors = [];
    const validFiles = [];

    // Check document count limit
    const totalDocumentsAfterUpload = currentDocuments + files.length;
    if (totalDocumentsAfterUpload > quotas.maxDocuments) {
      validationErrors.push(
        `Document limit exceeded. You can upload ${quotas.maxDocuments - currentDocuments} more document(s).`
      );
    }

    // Calculate total size of new files
    let totalNewFileSize = 0;
    
    files.forEach((file, index) => {
      // UPDATED: Check file type - only PDF, TXT, JPG, PNG allowed
      const isValidType = /\.(pdf|txt|jpg|jpeg|png)$/i.test(file.name);
      if (!isValidType) {
        validationErrors.push(`File "${file.name}" has an invalid format. Supported formats: PDF, TXT, JPG, PNG`);
        return;
      }

      // Check individual file size
      if (file.size > quotas.maxFileSize) {
        validationErrors.push(
          `File "${file.name}" (${formatFileSize(file.size)}) exceeds the ${formatFileSize(quotas.maxFileSize)} size limit.`
        );
        return;
      }

      totalNewFileSize += file.size;
      validFiles.push(file);
    });

    // Check total storage limit
    const currentStorageBytes = currentStorage * 1024 * 1024;
    const totalStorageAfterUpload = currentStorageBytes + totalNewFileSize;
    if (totalStorageAfterUpload > quotas.maxTotalStorage) {
      const availableStorage = quotas.maxTotalStorage - currentStorageBytes;
      validationErrors.push(
        `Storage limit exceeded. You have ${formatFileSize(availableStorage)} of storage remaining.`
      );
    }

    return { validFiles, validationErrors };
  };

  const handleDocumentChange = (event) => {
    const files = Array.from(event.target.files);
    setUploadErrors([]);
    
    const { validFiles, validationErrors } = validateDocuments(files);
    
    if (validationErrors.length > 0) {
      setUploadErrors(validationErrors);
      return;
    }
    
    // Add valid files to pending documents
    setPendingDocuments(prev => [...prev, ...validFiles.map(file => ({
      file,
      name: file.name,
      type: file.type,
      size: file.size
    }))]);
  };

  const uploadDocumentToS3 = async (file, presignedUrl) => {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-amz-meta-eventtype': 'BusinessDocuments',
          'x-amz-meta-documenttype': file.name.split('.').pop().toLowerCase()
        },
      });
  
      if (!response.ok) {
        throw new Error('Failed to upload document');
      }
  
      return true;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const handleUploadDocuments = async () => {
    if (pendingDocuments.length === 0) {
      setError('No documents to upload');
      return;
    }

    if (!canUploadDocuments || !hasStorageSpace) {
      setError('Upload quota exceeded. Please check your limits.');
      return;
    }

    try {
      setUploadState('uploading');
      setError('');

      const uploadedDocuments = [];
      
      for (const docInfo of pendingDocuments) {
        try {
          // Get presigned URL for each document
          const presignedUrlResponse = await getPreSignedDocs({
            eventType: 'BusinessDocuments',
            businessId: businessId,
            documentType: docInfo.file.name.split('.').pop().toLowerCase()
          }).unwrap();

          if (presignedUrlResponse?.url) {
            // Upload document
            await uploadDocumentToS3(docInfo.file, presignedUrlResponse.url);
            
            uploadedDocuments.push({
              name: docInfo.name,
              url: presignedUrlResponse.url.split('?')[0],
              keyPath: presignedUrlResponse.keyPath,
              size: docInfo.size
            });
          }
        } catch (error) {
          console.error(`Error uploading document ${docInfo.name}:`, error);
          setUploadErrors(prev => [...prev, `Failed to upload ${docInfo.name}`]);
        }
      }

      if (uploadedDocuments.length > 0) {
        setUploadSuccess(true);
        setUploadState('success');
        
        // Clear pending documents
        setPendingDocuments([]);
        
        // Success callback
        setTimeout(() => {
          onSuccess(uploadedDocuments);
        }, 1500);
      } else {
        throw new Error('No documents were uploaded successfully');
      }

    } catch (error) {
      console.error('Error uploading documents:', error);
      setError('Failed to upload documents. Please try again.');
      setUploadState('idle');
    }
  };

  const handleDocumentDelete = (type, index) => {
    if (type === 'pending') {
      // For pending documents, simply remove from state
      setPendingDocuments(prev => prev.filter((_, i) => i !== index));
    } else {
      // For existing documents, show confirmation dialog
      setDocumentToDelete({ type, index });
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteDocument = async () => {
    try {
      if (documentToDelete) {
        const { index } = documentToDelete;
        const docToDelete = existingDocuments[index];
        const fileName = docToDelete.name;
        
        await deleteBusinessDocument({
          businessId: businessId,
          documentKey: fileName
        }).unwrap();
        
        // Success callback to update parent component
        onSuccess({ action: 'delete', deletedIndex: index });
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setError('Failed to delete document. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const removePendingDocument = (index) => {
    setPendingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" />
          Smart Documents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload business documents and images (menus, catalogs, service lists, photos) to enable natural language queries from customers.
        </Typography>
      </Box>

      {/* Document Quota Display */}
      <DocumentQuotaDisplay 
        userAccountType={accountType}
        currentDocuments={currentDocuments}
        currentStorage={currentStorage}
        quotaLimits={quotas}
        isLoading={false}
      />

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>Upload failed:</Typography>
          {uploadErrors.map((error, index) => (
            <Typography key={index} variant="body2">• {error}</Typography>
          ))}
        </Alert>
      )}

      {/* Quota Limit Warning */}
      {(!canUploadDocuments || !hasStorageSpace) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {!canUploadDocuments 
              ? `Document limit reached (${currentDocuments}/${quotas.maxDocuments})`
              : 'Storage limit reached'
            }
            {accountType === 'free' && '. Upgrade to Premium for more storage!'}
          </Typography>
        </Alert>
      )}

      {/* Uploading State */}
      {uploadState === 'uploading' && (
        <Card variant="outlined" sx={{ bgcolor: 'info.light', borderColor: 'info.main', mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Uploading Documents...
            </Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Processing {pendingDocuments.length} document(s). AI analysis will begin automatically.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Upload Success State */}
      {uploadState === 'success' && (
        <Card variant="outlined" sx={{ bgcolor: 'success.light', borderColor: 'success.main', mb: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Upload Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your documents have been uploaded and AI processing has started. 
              Enhanced features will be available shortly.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Document Management Section */}
      {(uploadState === 'idle' || uploadState === 'success') && (
        <Card variant="outlined" sx={{ borderStyle: 'dashed', bgcolor: 'grey.50' }}>
          <CardContent>
            {/* Existing Documents */}
            {existingDocuments.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Documents
                </Typography>
                <Stack spacing={1}>
                  {existingDocuments.map((doc, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileTypeIcon(doc.name)}
                        <Typography variant="body2">{doc.name}</Typography>
                        {doc.size && (
                          <Chip 
                            label={formatFileSize(doc.size)} 
                            size="small" 
                            variant="outlined" 
                          />
                        )}
                        {isImageFile(doc.name) && (
                          <Chip 
                            label="Image" 
                            size="small" 
                            color="info"
                            variant="outlined" 
                          />
                        )}
                      </Box>
                      <Box>
                        <Tooltip title="View Document">
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(doc.url, '_blank')}
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Document">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDocumentDelete('existing', index)}
                            disabled={isDeleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* Pending Documents */}
            {pendingDocuments.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Documents Ready to Upload
                </Typography>
                <Stack spacing={1}>
                  {pendingDocuments.map((doc, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 1,
                      bgcolor: 'warning.light',
                      borderRadius: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileTypeIcon(doc.name)}
                        <Typography variant="body2">{doc.name}</Typography>
                        <Chip 
                          label={formatFileSize(doc.size)} 
                          size="small" 
                          variant="outlined" 
                        />
                        {isImageFile(doc.name) && (
                          <Chip 
                            label="Image" 
                            size="small" 
                            color="info"
                            variant="outlined" 
                          />
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => removePendingDocument(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* Upload Section */}
            <Box sx={{ textAlign: 'center' }}>
              <DocumentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              
              {existingDocuments.length === 0 && pendingDocuments.length === 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Upload Business Documents & Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add menus, catalogs, service lists, and photos to enhance customer experience
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add more documents or manage existing ones
                </Typography>
              )}

              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
                disabled={!canUploadDocuments || !hasStorageSpace || preSignedUrlDocsLoading}
                sx={{ mb: 2 }}
              >
                {preSignedUrlDocsLoading ? 'Processing...' : 
                 !canUploadDocuments ? 'Document Limit Reached' :
                 !hasStorageSpace ? 'Storage Limit Reached' :
                 'Choose Files'}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.txt,.jpg,.jpeg,.png"
                  onChange={handleDocumentChange}
                  multiple
                />
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Supported formats: PDF, TXT, JPG, PNG • Max {formatFileSize(quotas.maxFileSize)} per file
              </Typography>

              {pendingDocuments.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleUploadDocuments}
                    startIcon={<CloudUploadIcon />}
                    disabled={preSignedUrlDocsLoading || uploadState === 'uploading'}
                  >
                    Upload {pendingDocuments.length} File(s)
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this document? 
            This action cannot be undone, and the document will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDeleteDocument} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessDocumentUpload;